import {
  makeFunctionReference,
  type PaginationOptions,
  type PaginationResult,
} from "convex/server";

import type {
  CatalogCard,
  CatalogResponse,
  DiscoveryScope,
  EpisodeDetail,
  EpisodeLedgerRow,
  ShowCategory,
  ShowDetail,
  ShowOverview,
} from "../features/catalog/types";
import type {
  AccessType,
  CorrectionKind,
  EpisodeAvailability,
} from "../features/availability/types";
import type { CalendarWindowResult } from "../features/calendar/types";
import type {
  ActivityEntry,
  Comment,
  CommentReply,
  InvitePreview,
  InviteSummary,
  PendingFollowRequest,
  PublicProfile,
  ReactionKind,
  RelationshipSummary,
  ReportReason,
} from "../features/social/types";
import type {
  NotificationPreferences,
  PushSubscriptionSummary,
} from "../features/notifications/types";
import type {
  EpisodeTrackingState,
  FollowedShowsResult,
  ShowTrackingSummary,
  TrackingMutationResult,
  TrackingMutationResultV2,
  TrackingOperation,
  UpNextEntry,
} from "../features/tracking/types";

// Keep this narrow browser-facing contract synchronized with the registered functions. Importing
// the generated data-model declaration currently exposes Convex Auth's optional table fields to the
// application's stricter exactOptionalPropertyTypes build, so the frontend owns only the references
// and result shapes it consumes.

export type TitleDisplayPreference = "english" | "native" | "romanized";
export type ProfileVisibility = "private" | "public";

export interface ViewerProfile {
  userId: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  countryCode: string | null;
  timeZone: string | null;
  formattingLocale: string;
  titleDisplayPreference: TitleDisplayPreference;
  profileVisibility: ProfileVisibility;
  activityVisibility: ProfileVisibility;
  onboardingCompleted: boolean;
}

export interface AuthCapabilities {
  apple: boolean;
  email: boolean;
  google: boolean;
}

export interface AccountOverview {
  email: string | null;
  providers: string[];
  currentSessionCreatedAt: number;
  currentSessionExpiresAt: number;
  otherSessionCount: number;
  recentlyAuthenticated: boolean;
}

export const backendApi = {
  account: {
    overview: makeFunctionReference<"query", { now: number }, AccountOverview>("account:overview"),
    revokeOtherSessions: makeFunctionReference<
      "mutation",
      Record<string, never>,
      { revoked: number }
    >("account:revokeOtherSessions"),
    exportAccount: makeFunctionReference<
      "query",
      Record<string, never>,
      { exportedAt: number; truncated: string[]; data: Record<string, unknown[]> }
    >("account:exportAccount"),
    deleteAccount: makeFunctionReference<"mutation", { confirmation: string }, null>(
      "account:deleteAccount",
    ),
  },
  authCapabilities: {
    get: makeFunctionReference<"query", Record<string, never>, AuthCapabilities>(
      "authCapabilities:get",
    ),
  },
  catalog: {
    search: makeFunctionReference<
      "query",
      { query: string; category: ShowCategory | null; limit: number },
      CatalogCard[]
    >("catalog:search"),
    listDiscovery: makeFunctionReference<
      "query",
      { scope: DiscoveryScope; limit: number; now: number },
      CatalogCard[]
    >("catalog:listDiscovery"),
    showByPublicId: makeFunctionReference<"query", { publicId: string }, ShowDetail | null>(
      "catalog:showByPublicId",
    ),
    showOverviewByPublicId: makeFunctionReference<
      "query",
      { publicId: string },
      ShowOverview | null
    >("catalog:showOverviewByPublicId"),
    showRefreshStatus: makeFunctionReference<
      "query",
      { publicId: string },
      {
        status: "queued" | "running" | "failed" | "complete";
        completedInstallments: number;
        totalInstallments: number;
        errorCode: string | null;
        updatedAt: number;
      } | null
    >("catalog:showRefreshStatus"),
    episodeByPublicId: makeFunctionReference<"query", { publicId: string }, EpisodeDetail | null>(
      "catalog:episodeByPublicId",
    ),
  },
  notificationCapabilities: {
    get: makeFunctionReference<
      "query",
      Record<string, never>,
      { push: boolean; publicKey: string | null }
    >("notificationCapabilities:get"),
  },
  notifications: {
    getPreferences: makeFunctionReference<"query", Record<string, never>, NotificationPreferences>(
      "notifications:getPreferences",
    ),
    /** A patch: an omitted field is left alone, so one control cannot undo another. */
    updatePreferences: makeFunctionReference<
      "mutation",
      Partial<NotificationPreferences>,
      NotificationPreferences
    >("notifications:updatePreferences"),
    listSubscriptions: makeFunctionReference<
      "query",
      { currentEndpoint?: string },
      PushSubscriptionSummary[]
    >("notifications:listSubscriptions"),
    subscribe: makeFunctionReference<
      "mutation",
      { endpoint: string; p256dh: string; auth: string; deviceLabel: string },
      null
    >("notifications:subscribe"),
    unsubscribe: makeFunctionReference<"mutation", { subscriptionId: string }, null>(
      "notifications:unsubscribe",
    ),
    /** Scoped to one browser: signing out here is not a decision about other devices. */
    unsubscribeThisDevice: makeFunctionReference<"mutation", { endpoint: string }, null>(
      "notifications:unsubscribeThisDevice",
    ),
  },
  calendar: {
    listReleases: makeFunctionReference<
      "query",
      { fromDate: string; toDate: string; networks?: string[] },
      CalendarWindowResult
    >("calendar:listReleases"),
  },
  relationships: {
    relationshipWith: makeFunctionReference<"query", { userId: string }, PublicProfile>(
      "relationships:relationshipWith",
    ),
    /** Exact username only. There is no prefix search and no way to list accounts. */
    byUsername: makeFunctionReference<"query", { username: string }, PublicProfile | null>(
      "relationships:byUsername",
    ),
    followers: makeFunctionReference<
      "query",
      { userId: string; paginationOpts: PaginationOptions },
      PaginationResult<RelationshipSummary>
    >("relationships:followers"),
    following: makeFunctionReference<
      "query",
      { userId: string; paginationOpts: PaginationOptions },
      PaginationResult<RelationshipSummary>
    >("relationships:following"),
    pendingFollowRequests: makeFunctionReference<
      "query",
      { paginationOpts: PaginationOptions },
      PaginationResult<PendingFollowRequest>
    >("relationships:pendingFollowRequests"),
    blockedAccounts: makeFunctionReference<
      "query",
      { paginationOpts: PaginationOptions },
      PaginationResult<RelationshipSummary>
    >("relationships:blockedAccounts"),
    follow: makeFunctionReference<"mutation", { userId: string }, "following" | "requested">(
      "relationships:follow",
    ),
    unfollow: makeFunctionReference<"mutation", { userId: string }, null>("relationships:unfollow"),
    removeFollower: makeFunctionReference<"mutation", { userId: string }, null>(
      "relationships:removeFollower",
    ),
    respondToFollowRequest: makeFunctionReference<
      "mutation",
      { requestId: string; accept: boolean },
      null
    >("relationships:respondToFollowRequest"),
    block: makeFunctionReference<"mutation", { userId: string }, null>("relationships:block"),
    unblock: makeFunctionReference<"mutation", { userId: string }, null>("relationships:unblock"),
    mute: makeFunctionReference<"mutation", { userId: string }, null>("relationships:mute"),
    unmute: makeFunctionReference<"mutation", { userId: string }, null>("relationships:unmute"),
  },
  invites: {
    mine: makeFunctionReference<"query", Record<string, never>, InviteSummary[]>("invites:mine"),
    revoke: makeFunctionReference<"mutation", { inviteId: string }, null>("invites:revoke"),
    /** Returns the plaintext token once. There is no way to read it back. */
    create: makeFunctionReference<
      "action",
      { intent: "follow" | "mutual"; lifetimeMs: number; maxUses: number },
      { token: string; inviteId: string }
    >("inviteTokens:create"),
    redeem: makeFunctionReference<
      "action",
      { token: string },
      { createdBy: string; intent: "follow" | "mutual"; alreadyRedeemed: boolean }
    >("inviteTokens:redeem"),
    preview: makeFunctionReference<"action", { token: string }, InvitePreview | null>(
      "inviteTokens:preview",
    ),
  },
  comments: {
    forEpisode: makeFunctionReference<
      "query",
      {
        episodePublicId: string;
        revealedCommentIds?: string[];
        paginationOpts: PaginationOptions;
      },
      PaginationResult<Comment>
    >("comments:forEpisode"),
    /** The rest of one thread, for a reader who asks past the attached few. */
    repliesFor: makeFunctionReference<
      "query",
      {
        commentId: string;
        revealedCommentIds?: string[];
        paginationOpts: PaginationOptions;
      },
      PaginationResult<CommentReply>
    >("comments:repliesFor"),
    /** Separate from the list, so opening a thread never sends an unrevealed spoiler. */
    revealSpoiler: makeFunctionReference<"query", { commentId: string }, string | null>(
      "comments:revealSpoiler",
    ),
    post: makeFunctionReference<
      "mutation",
      { episodePublicId: string; body: string; isSpoiler: boolean; parentId?: string },
      string
    >("comments:post"),
    edit: makeFunctionReference<
      "mutation",
      { commentId: string; body: string; isSpoiler: boolean },
      null
    >("comments:edit"),
    remove: makeFunctionReference<"mutation", { commentId: string }, null>("comments:remove"),
    react: makeFunctionReference<
      "mutation",
      { commentId: string; kind: ReactionKind | null },
      null
    >("comments:react"),
  },
  activity: {
    feed: makeFunctionReference<"query", Record<string, never>, ActivityEntry[]>("activity:feed"),
    forAccount: makeFunctionReference<
      "query",
      { userId: string; paginationOpts: PaginationOptions },
      PaginationResult<ActivityEntry>
    >("activity:forAccount"),
  },
  availability: {
    /** Territory is explicit so a reader can look elsewhere without changing their account. */
    forEpisode: makeFunctionReference<
      "query",
      { episodePublicId: string; territory: string; now: number },
      EpisodeAvailability
    >("availability:forEpisode"),
    submitCorrection: makeFunctionReference<
      "mutation",
      {
        availabilityId?: string;
        episodePublicId: string;
        territory: string;
        kind: CorrectionKind;
        evidence?: string;
        suggestedProviderName?: string;
        suggestedAccessType?: AccessType;
      },
      null
    >("availability:submitCorrection"),
    refresh: makeFunctionReference<
      "action",
      { publicId: string },
      { rows: number; scopes: number }
    >("metadataActions:refreshAvailability"),
  },
  moderation: {
    report: makeFunctionReference<
      "mutation",
      {
        targetCommentId?: string;
        targetUserId?: string;
        reason: ReportReason;
        evidence?: string;
      },
      null
    >("moderation:report"),
  },
  ratings: {
    /** Half stars as an integer 1..10; the surface divides by two once. */
    episodeRating: makeFunctionReference<
      "query",
      { episodePublicId: string },
      {
        averageHalfStars: number | null;
        count: number;
        sampled: boolean;
        viewerHalfStars: number | null;
      }
    >("ratings:episodeRating"),
    rateEpisode: makeFunctionReference<
      "mutation",
      { episodePublicId: string; halfStars: number },
      null
    >("ratings:rateEpisode"),
    clearEpisodeRating: makeFunctionReference<"mutation", { episodePublicId: string }, null>(
      "ratings:clearEpisodeRating",
    ),
  },
  metadata: {
    search: makeFunctionReference<
      "action",
      { query: string; category: ShowCategory | "all" },
      CatalogResponse
    >("metadataActions:search"),
    refreshDiscovery: makeFunctionReference<"action", { scope: DiscoveryScope }, CatalogResponse>(
      "metadataActions:refreshDiscovery",
    ),
    refreshShow: makeFunctionReference<
      "action",
      { publicId: string },
      { publicId: string; refreshedAt: number }
    >("metadataActions:refreshShow"),
  },
  profiles: {
    viewer: makeFunctionReference<"query", Record<string, never>, ViewerProfile | null>(
      "profiles:viewer",
    ),
    bootstrap: makeFunctionReference<
      "mutation",
      { countryCode?: string; formattingLocale?: string; timeZone?: string },
      null
    >("profiles:bootstrap"),
    completeOnboarding: makeFunctionReference<
      "mutation",
      {
        username: string;
        displayName: string;
        countryCode: string;
        timeZone: string;
        formattingLocale: string;
        titleDisplayPreference: TitleDisplayPreference;
      },
      null
    >("profiles:completeOnboarding"),
    updateIdentity: makeFunctionReference<
      "mutation",
      { displayName: string; username: string },
      null
    >("profiles:updateIdentity"),
    updateRegionalPreferences: makeFunctionReference<
      "mutation",
      {
        countryCode: string;
        formattingLocale: string;
        timeZone: string;
        titleDisplayPreference: TitleDisplayPreference;
      },
      null
    >("profiles:updateRegionalPreferences"),
    updatePrivacy: makeFunctionReference<
      "mutation",
      { activityVisibility: ProfileVisibility; profileVisibility: ProfileVisibility },
      null
    >("profiles:updatePrivacy"),
  },
  tracking: {
    applyOperation: makeFunctionReference<
      "mutation",
      { operationId: string; idempotencyKey: string; operation: TrackingOperation },
      TrackingMutationResult
    >("tracking:applyOperation"),
    episodeTrackingState: makeFunctionReference<
      "query",
      { showPublicId: string; episodePublicId: string },
      EpisodeTrackingState
    >("tracking:episodeTrackingState"),
    showSummary: makeFunctionReference<
      "query",
      { showPublicId: string; now: number },
      ShowTrackingSummary
    >("tracking:showSummary"),
    listInstallmentEpisodeRows: makeFunctionReference<
      "query",
      {
        installmentPublicId: string;
        paginationOpts: { numItems: number; cursor: string | null };
      },
      {
        page: EpisodeLedgerRow[];
        isDone: boolean;
        continueCursor: string;
      }
    >("tracking:listInstallmentEpisodeRows"),
    applyOperationV2: makeFunctionReference<
      "mutation",
      { operationId: string; idempotencyKey: string; operation: TrackingOperation },
      TrackingMutationResultV2
    >("tracking:applyOperationV2"),
    listUpNextEntries: makeFunctionReference<"query", Record<string, never>, UpNextEntry[]>(
      "tracking:listUpNextEntries",
    ),
    /** Every followed show, capped; My Lists searches and filters the result. */
    listFollowedShows: makeFunctionReference<"query", Record<string, never>, FollowedShowsResult>(
      "tracking:listFollowedShows",
    ),
  },
} as const;
